import React, { useEffect, useState, useCallback } from "react";
import { Search, X, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import Pagination from "@/components/Pagination";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import ConfirmDialog from "@/components/ConfirmDialog";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const pretty = (s) => (s || "").replace(/_/g, " ");
const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleString("en-US", { timeZone: "America/Chicago", year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
};

export default function AuditLogs() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [name, setName] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [actionType, setActionType] = useState("all");
  const [role, setRole] = useState("all");
  const [entityType, setEntityType] = useState("all");
  const [campus, setCampus] = useState("all");
  const [team, setTeam] = useState("all");
  const [facets, setFacets] = useState({ action_types: [], entity_types: [], roles: [], campuses: [], teams: [] });
  const [exporting, setExporting] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    api.get("/admin/audit-logs/facets").then(({ data }) => setFacets(data)).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (name.trim()) params.set("q", name.trim());
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    if (actionType !== "all") params.set("action_type", actionType);
    if (role !== "all") params.set("role", role);
    if (entityType !== "all") params.set("entity_type", entityType);
    if (campus !== "all") params.set("campus_id", campus);
    if (team !== "all") params.set("team_id", team);
    return params;
  }, [name, start, end, actionType, role, entityType, campus, team]);

  const load = useCallback(async () => {
    const params = buildParams();
    params.set("page", String(page));
    params.set("page_size", "30");
    const { data } = await api.get(`/admin/audit-logs?${params}`);
    setItems(data.items); setTotal(data.total);
  }, [buildParams, page]);
  useEffect(() => { load(); }, [load]);

  const clear = () => { setName(""); setStart(""); setEnd(""); setActionType("all"); setRole("all"); setEntityType("all"); setCampus("all"); setTeam("all"); setPage(1); };
  const hasFilters = name || start || end || actionType !== "all" || role !== "all" || entityType !== "all" || campus !== "all" || team !== "all";
  const pick = (setter) => (v) => { setter(v); setPage(1); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${BASE}/admin/audit-logs/export?${buildParams()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rallypointgo_token")}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "audit_logs.csv";
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Audit logs exported");
    } catch (e) { toast.error(e.message); }
    finally { setExporting(false); }
  };

  const clearLog = async () => {
    setClearing(true);
    try {
      const { data } = await api.delete("/admin/audit-logs");
      toast.success(`Audit log cleared — ${data.deleted} entr${data.deleted === 1 ? "y" : "ies"} deleted`);
      setClearOpen(false);
      clear();
      load();
      api.get("/admin/audit-logs/facets").then(({ data }) => setFacets(data)).catch(() => {});
    } catch (e) { toast.error("Failed to clear audit log"); }
    finally { setClearing(false); }
  };

  return (
    <div>
      <PageHeader
        title="Audit Logs"
        subtitle="System activity across all modules · retained permanently"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-full" onClick={exportCsv} disabled={exporting} data-testid="audit-export-btn">
              <Download className="mr-2 h-4 w-4" /> {exporting ? "Exporting…" : "Export CSV"}
            </Button>
            <Button variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" onClick={() => setClearOpen(true)} disabled={clearing || total === 0} data-testid="audit-clear-btn">
              <Trash2 className="mr-2 h-4 w-4" /> Clear log
            </Button>
          </div>
        }
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={name} onChange={(e) => { setName(e.target.value); setPage(1); }} placeholder="Filter by user name" className="w-52 pl-9" data-testid="audit-name-filter" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Action</Label>
          <Select value={actionType} onValueChange={pick(setActionType)}>
            <SelectTrigger className="w-44" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {facets.action_types.map((a) => <SelectItem key={a} value={a}>{pretty(a)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Role</Label>
          <Select value={role} onValueChange={pick(setRole)}>
            <SelectTrigger className="w-36" data-testid="audit-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {facets.roles.map((r) => <SelectItem key={r} value={r}>{pretty(r)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Entity</Label>
          <Select value={entityType} onValueChange={pick(setEntityType)}>
            <SelectTrigger className="w-40" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              {facets.entity_types.map((e) => <SelectItem key={e} value={e}>{pretty(e)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">School</Label>
          <Select value={campus} onValueChange={pick(setCampus)}>
            <SelectTrigger className="w-44" data-testid="audit-campus-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All schools</SelectItem>
              {facets.campuses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Team</Label>
          <Select value={team} onValueChange={pick(setTeam)}>
            <SelectTrigger className="w-44" data-testid="audit-team-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teams</SelectItem>
              {facets.teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">From</Label>
          <Input type="date" value={start} onChange={(e) => { setStart(e.target.value); setPage(1); }} className="w-40" data-testid="audit-start-filter" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">To</Label>
          <Input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPage(1); }} className="w-40" data-testid="audit-end-filter" />
        </div>
        {hasFilters && <Button variant="ghost" onClick={clear} className="text-slate-500" data-testid="audit-clear-filters"><X className="mr-1 h-4 w-4" /> Clear</Button>}
        <span className="ml-auto text-sm text-slate-500" data-testid="audit-count">{total} entries</span>
      </div>
      {items.length === 0 ? <EmptyState title="No audit entries" description={hasFilters ? "No entries match your filters." : ""} testId="audit-empty" /> : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table data-testid="audit-table">
            <TableHeader><TableRow className="bg-slate-50">{["When", "User", "Role", "Action", "Entity", "Campus", "Team", "Summary"].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">{fmtDateTime(a.timestamp)}</TableCell>
                  <TableCell className="text-sm font-medium">{a.acting_user_name || "—"}</TableCell>
                  <TableCell className="text-xs"><Badge variant="secondary">{a.role}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{a.action_type}</TableCell>
                  <TableCell className="text-xs text-slate-500">{a.entity_type}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500" data-testid={`audit-campus-${a.id}`}>{a.campus_name || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500" data-testid={`audit-team-${a.id}`}>{a.team_name || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{a.change_summary}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <Pagination page={page} pageSize={30} total={total} onPage={setPage} />
      <ConfirmDialog
        open={clearOpen}
        onOpenChange={(o) => !o && setClearOpen(false)}
        title="Clear the entire audit log?"
        description={`This permanently deletes all ${total} audit log entr${total === 1 ? "y" : "ies"} to start clean. This cannot be undone. A single new entry will record that you performed this wipe.`}
        confirmText={clearing ? "Clearing…" : "Clear log"}
        destructive
        onConfirm={clearLog}
      />
    </div>
  );
}
